<template>
  <!-- version2026 settings shell: white menu column (queue-column recipe, copied not shared) + bare router-view. -->
  <div class="v2026-settings">
    <nav class="v2026-settings-menu"
         :class="{ 'is-collapsed': collapsed }"
         aria-label="เมนูการตั้งค่า">
      <div class="v2026-settings-title">
        <span v-if="!collapsed" class="v2026-settings-title-text">การตั้งค่า</span>
        <button type="button"
                class="v2026-settings-collapse"
                :aria-expanded="!collapsed"
                :aria-label="collapsed ? 'ขยายเมนูการตั้งค่า' : 'ย่อเมนูการตั้งค่า'"
                :title="collapsed ? 'ขยายเมนูการตั้งค่า' : 'ย่อเมนูการตั้งค่า'"
                @click="collapsed = !collapsed">
          <span class="v2026-settings-collapse-ico" aria-hidden="true"></span>
        </button>
      </div>

      <div class="v2026-settings-list">
        <!-- activeClass/exactActiveClass are neutralised ON PURPOSE: vue-router ignores the query,
             so its own matching would light BOTH tag rows on ?system=1. isActive() is the only
             thing allowed to select a row. -->
        <RouterLink v-for="item in menu"
                    :key="item.key"
                    :to="item.to"
                    class="v2026-settings-row"
                    :class="{ 'is-selected': isActive(item) }"
                    :title="item.label"
                    :aria-label="collapsed ? item.label : null"
                    :aria-current="isActive(item) ? 'page' : null"
                    activeClass=""
                    exactActiveClass="">
          <span class="v2026-settings-mask"
                aria-hidden="true"
                :style="maskStyle(icons[item.key])"></span>
          <span v-if="!collapsed" class="v2026-settings-label">{{ item.label }}</span>
        </RouterLink>
      </div>
    </nav>

    <div class="v2026-settings-content">
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
export default {
  name: 'settings',
  data () {
    return {
      // Collapse is plain in-memory state — no localStorage, no store, no query param. Resets on
      // reload, exactly like queueCollapsed in Conversation.vue.
      collapsed: false,
      // Order and labels are fixed; `to` stays a plain string so the rendered hrefs remain
      // byte-identical to the pre-redesign menu (including the ?system=1 query).
      menu: [
        { key: 'messageTemplate', label: 'ตั้งค่าคีย์ลัด', to: '/settings/message-template' },
        { key: 'tag', label: 'แท็ก', to: '/settings/tag' },
        { key: 'topic', label: 'หัวข้อสนทนา', to: '/settings/tag?system=1' },
        { key: 'autoMessage', label: 'ข้อความตอบกลับอัตโนมัติ', to: '/settings/auto-message' },
        { key: 'lineAudience', label: 'Line Audience Group', to: '/settings/line-audience' },
        { key: 'serverSettings', label: 'จัดการ Chat Server', to: '/settings/server-settings' },
        { key: 'aiDocuments', label: 'จัดการ AI Documents', to: '/settings/ai-documents' }
      ],
      // Row icons: build-resolved svg URLs painted via CSS mask, same technique as the queue rows.
      icons: {
        messageTemplate: require('@/assets/icons2026/keyboard.svg'),
        tag: require('@/assets/icons2026/tag.svg'),
        topic: require('@/assets/icons2026/topic.svg'),
        autoMessage: require('@/assets/icons2026/auto-reply.svg'),
        lineAudience: require('@/assets/icons2026/line.svg'),
        serverSettings: require('@/assets/icons2026/server.svg'),
        aiDocuments: require('@/assets/icons2026/ai-magic.svg')
      }
    }
  },
  methods: {
    // Hand-written active rule. Exactly one row is lit on every settings URL, none on bare /settings.
    isActive (item) {
      const path = this.$route.path
      const system = String(this.$route.query.system || '')

      if (item.key === 'messageTemplate') {
        // the category sub-page has no row of its own → row 1 stays lit
        return path === '/settings/message-template' ||
               path === '/settings/message-template-category'
      }
      if (item.key === 'tag') return path === '/settings/tag' && system !== '1'
      if (item.key === 'topic') return path === '/settings/tag' && system === '1'
      return path === item.to
    },
    maskStyle (url) {
      return { '-webkit-mask-image': 'url(' + url + ')', 'mask-image': 'url(' + url + ')' }
    },
    // Single threshold, in JS (not a media query) so is-collapsed stays the one source of truth for
    // the collapsed styling. A manual toggle survives until the next resize event — same contract as
    // Conversation.vue's applyResponsiveCollapse().
    applyResponsiveCollapse () {
      this.collapsed = window.innerWidth < 992
    }
  },
  mounted () {
    this.applyResponsiveCollapse()
    window.addEventListener('resize', this.applyResponsiveCollapse)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.applyResponsiveCollapse)
  }
}
</script>

<style lang="scss" scoped>
/* The parent .v2026-main is exactly 100vh - header and has overflow:hidden, so the scroll container
   has to live here. The legacy height-without-nav (calc(100vh - 49px), an old 49px navbar) is gone:
   it was 15px too tall for the 64px v2026 header and left ~959px of /settings/ai-documents
   unreachable. */
.v2026-settings {
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #ffffff;
}

/* Column chrome copied from .v2026-col-queue, but it collapses to a 64px icon rail instead of 0 —
   the header button is the re-expand affordance, so the border-right and the icons must stay. */
.v2026-settings-menu {
  flex: 0 0 240px;
  width: 240px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border-right: 1px solid #e9ecef;
  overflow: hidden;
  transition: flex-basis .2s ease, width .2s ease;

  &.is-collapsed {
    flex: 0 0 64px;
    width: 64px;
  }
}

.v2026-settings-title {
  display: flex;
  align-items: center;
  min-height: 54px;
  /* 13 + 28 (button) + 13 = 54px, i.e. the queue header height with a bigger hit target. */
  padding: 13px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #212529;
  border-bottom: 1px solid #e9ecef;

  .is-collapsed & {
    padding: 13px 0;
  }
}

.v2026-settings-title-text {
  white-space: nowrap;
}

.v2026-settings-collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: auto;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: none;
  cursor: pointer;

  &:hover {
    background: var(--v2026-color-hover-bg);

    .v2026-settings-collapse-ico {
      background-color: #212529;
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }

  .is-collapsed & {
    margin: 0 auto;
  }
}

.v2026-settings-collapse-ico {
  display: inline-block;
  width: 18px;
  height: 18px;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask: url('~@/assets/icons2026/arrow-right.svg') center / contain no-repeat;
  mask: url('~@/assets/icons2026/arrow-right.svg') center / contain no-repeat;

  /* The base svg draws a left-pointing arrow into a bar (|←) = "collapse". Collapsed, the SAME
     button re-expands, so the glyph is flipped to →|. */
  .is-collapsed & {
    transform: rotate(180deg);
  }
}

/* On a short viewport the seven rows scroll inside the column instead of being clipped. */
.v2026-settings-list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px 0;
}

/* Rows stay real anchors (router-link) so middle-click / open-in-new-tab / the status-bar URL keep
   working — the queue's role="button" divs are deliberately NOT copied here. */
.v2026-settings-row {
  display: flex;
  align-items: center;
  gap: 14px;
  height: 50px;
  margin: 2px 8px;
  padding: 0 8px;
  border-radius: 8px;
  position: relative;
  font-size: 15px;
  color: #343a40;
  text-decoration: none;
  cursor: pointer;

  /* :not(.is-selected) so hover can never repaint the selected tint grey. */
  &:hover:not(.is-selected) {
    background: var(--v2026-color-hover-bg);
  }

  &:hover,
  &:focus {
    color: #343a40;
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }

  &.is-selected {
    background: var(--v2026-color-main-soft);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 3px;
      border-radius: 0 2px 2px 0;
      background: var(--v2026-color-main-bar);
    }

    .v2026-settings-label {
      color: var(--v2026-color-main-icon);
      font-weight: 600;
    }

    .v2026-settings-mask {
      background-color: var(--v2026-color-main-icon);
    }
  }

  .is-collapsed & {
    justify-content: center;
    gap: 0;
    padding: 0;

    .v2026-settings-mask {
      width: 20px;
      height: 20px;
    }
  }
}

/* CSS-mask row icon: the require()'d svg is the mask, background-color is the actual paint, so the
   icon renders an EXACT token colour regardless of the source SVG fills. */
.v2026-settings-mask {
  flex: 0 0 auto;
  display: inline-block;
  width: 18px;
  height: 18px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-color-icon-idle);
}

/* Single line always: a font substitution can only ever produce an ellipsis, never a 2-line row. */
.v2026-settings-label {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-settings-content {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #ffffff;
}

@media (prefers-reduced-motion: reduce) {
  .v2026-settings-menu {
    transition: none;
  }
}
</style>
